import React, { useEffect, useState } from 'react';
import { useMedicationStore } from '../lib/store';
import { Plus, Edit, Trash2 } from 'lucide-react';

interface MedicationFormData {
  name: string;
  dosage: string;
  frequency: string;
  prescription_number?: string;
  refills_remaining?: number;
  next_refill_date?: string;
  pharmacy_name?: string;
  pharmacy_phone?: string;
  notes?: string;
}

function Medications() {
  const { medications, loadMedications, addMedication, updateMedication } = useMedicationStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMedication, setEditingMedication] = useState<any>(null);
  const [formData, setFormData] = useState<MedicationFormData>({
    name: '',
    dosage: '',
    frequency: '',
  });

  useEffect(() => {
    loadMedications();
  }, [loadMedications]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingMedication) {
        await updateMedication(editingMedication.id, formData);
      } else {
        await addMedication(formData);
      }
      setIsModalOpen(false);
      setEditingMedication(null);
      setFormData({
        name: '',
        dosage: '',
        frequency: '',
      });
    } catch (error) {
      console.error('Error saving medication:', error);
    }
  };

  const handleEdit = (medication: any) => {
    setEditingMedication(medication);
    setFormData(medication);
    setIsModalOpen(true);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">My Medications</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Medication
        </button>
      </div>

      {/* Medications List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {medications.map((medication: any) => (
          <div key={medication.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold text-gray-800">{medication.name}</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(medication)}
                  className="text-gray-600 hover:text-blue-600"
                >
                  <Edit className="h-5 w-5" />
                </button>
                <button className="text-gray-600 hover:text-red-600">
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-gray-600">
                <span className="font-medium">Dosage:</span> {medication.dosage}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Frequency:</span> {medication.frequency}
              </p>
              {medication.refills_remaining !== null && (
                <p className="text-gray-600">
                  <span className="font-medium">Refills Remaining:</span>{' '}
                  {medication.refills_remaining}
                </p>
              )}
              {medication.next_refill_date && (
                <p className="text-gray-600">
                  <span className="font-medium">Next Refill:</span>{' '}
                  {new Date(medication.next_refill_date).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Medication Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">
              {editingMedication ? 'Edit' : 'Add'} Medication
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Medication Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Dosage
                </label>
                <input
                  type="text"
                  value={formData.dosage}
                  onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Frequency
                </label>
                <input
                  type="text"
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Prescription Number
                </label>
                <input
                  type="text"
                  value={formData.prescription_number || ''}
                  onChange={(e) => setFormData({ ...formData, prescription_number: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Refills Remaining
                </label>
                <input
                  type="number"
                  value={formData.refills_remaining || ''}
                  onChange={(e) => setFormData({ ...formData, refills_remaining: parseInt(e.target.value) })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Next Refill Date
                </label>
                <input
                  type="date"
                  value={formData.next_refill_date || ''}
                  onChange={(e) => setFormData({ ...formData, next_refill_date: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingMedication(null);
                    setFormData({ name: '', dosage: '', frequency: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingMedication ? 'Save Changes' : 'Add Medication'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Medications;